import { cn, describeVisibility } from "@vellum/ui";
import { ReportScope, type ReportSchema } from "@vellum/vellum-api";
import { Link } from "react-router-dom";

const CARD_CLASSES =
  "relative flex min-h-42 min-w-40 flex-col overflow-hidden rounded-2xl border bg-white p-3 lg:p-5 shadow-sm";

const ICON_TILE_CLASSES =
  "flex h-8 md:h-10 w-8 md:w-10 items-center justify-center rounded-xl bg-teal-600/10 text-teal-600 ring-1 ring-teal-600/10";

const SCOPE_LABELS: Record<ReportScope, string> = {
  [ReportScope.NONE]: "No scope",
  [ReportScope.COMPANY]: "By company",
  [ReportScope.COMPANY_FACILITY]: "By facility",
};

/** "Daily · By facility" — what you'll be asked for, and over what window. */
function describeScope(report: ReportSchema): string {
  return [report.cadence ?? "No date filter", SCOPE_LABELS[report.scope]].join(
    " · ",
  );
}

function CardBody({ report }: { report: ReportSchema }) {
  return (
    <>
      <h3 className="mt-4 mb-1 text-sm md:text-base font-semibold tracking-tight text-teal-600">
        {report.title}
      </h3>
      <p className="m-0 flex-1 text-xs md:text-sm leading-snug text-gray-600">
        {report.description}
      </p>
    </>
  );
}

export function ReportCard({ report }: { report: ReportSchema }) {
  return (
    <Link
      to={`/reports/${report.slug}`}
      className={cn(
        CARD_CLASSES,
        "group border-teal-600/10 no-underline transition-all duration-200 hover:-translate-y-0.5 hover:border-teal-600/40 hover:shadow-lg hover:shadow-teal-600/10 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500",
      )}
    >
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 top-0 h-0.5 bg-linear-to-r from-teal-600/0 via-teal-600 to-teal-600/0 opacity-0 transition-opacity duration-200 group-hover:opacity-100"
      />

      <div className="flex items-start justify-between">
        <span
          className={cn(
            ICON_TILE_CLASSES,
            "transition-colors duration-200 group-hover:bg-teal-600 group-hover:text-white",
          )}
        >
          <span
            className={`iconoir-${report.icon ?? "reports"} text-lg md:text-xl`}
            aria-hidden="true"
          />
        </span>
        <span
          aria-hidden="true"
          className="iconoir-arrow-right text-lg text-gray-300 transition-all duration-200 group-hover:translate-x-0.5 group-hover:text-teal-600"
        />
      </div>

      <CardBody report={report} />
      <span className="mt-3 text-xs text-gray-500">
        {describeScope(report)}
      </span>
    </Link>
  );
}

interface EditableReportCardProps {
  report: ReportSchema;
  onEdit: (report: ReportSchema) => void;
  onDelete: (report: ReportSchema) => void;
  onDragStart: (report: ReportSchema) => void;
  onDragEnd: () => void;
  onDropOn: (target: ReportSchema) => void;
  dragging: boolean;
}

/**
 * The same card the viewer sees, plus the manage-mode affordances: edit,
 * delete, a visibility summary, and drag handles for reordering.
 */
export function EditableReportCard({
  report,
  onEdit,
  onDelete,
  onDragStart,
  onDragEnd,
  onDropOn,
  dragging,
}: EditableReportCardProps) {
  return (
    <div
      draggable
      onDragStart={() => onDragStart(report)}
      onDragEnd={onDragEnd}
      onDragOver={(event) => event.preventDefault()}
      onDrop={(event) => {
        event.preventDefault();
        event.stopPropagation();
        onDropOn(report);
      }}
      className={cn(
        CARD_CLASSES,
        "cursor-grab border-dashed border-teal-600/20 active:cursor-grabbing",
        dragging && "opacity-40",
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <span className={ICON_TILE_CLASSES}>
          <span
            className={`iconoir-${report.icon ?? "reports"} text-xl`}
            aria-hidden="true"
          />
        </span>
        <div className="flex items-center gap-1">
          <span
            className="iconoir-drag flex h-7 w-7 pt-1 items-center justify-center text-lg text-gray-300"
            aria-hidden="true"
            title="Drag to reorder"
          />
          <button
            type="button"
            aria-label={`Edit ${report.title}`}
            onClick={() => onEdit(report)}
            className="flex h-7 w-7 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-blue-500/10 hover:text-blue-500"
          >
            <span className="iconoir-edit-pencil text-lg" aria-hidden="true" />
          </button>
          {/* A protected report is linked to from outside this app, so the
              API refuses to delete it — don't offer a button that 409s. */}
          {report.protected ? (
            <span
              className="iconoir-lock flex h-7 w-7 items-center justify-center text-lg text-gray-300"
              title="Linked to from outside the reporting app, so it cannot be deleted"
              aria-label={`${report.title} cannot be deleted`}
            />
          ) : (
            <button
              type="button"
              aria-label={`Delete ${report.title}`}
              onClick={() => onDelete(report)}
              className="flex h-7 w-7 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-error/10 hover:text-error"
            >
              <span className="iconoir-trash text-lg" aria-hidden="true" />
            </button>
          )}
        </div>
      </div>
      <CardBody report={report} />
      <span className="mt-3 text-xs text-gray-500">
        {describeScope(report)} · question {report.metabase_question_id}
      </span>
      <span className="text-xs text-gray-500">
        Visible to:{" "}
        <span className="font-semibold">
          {describeVisibility(report.visible_to_roles)}
        </span>
      </span>
    </div>
  );
}

export function AddReportCard({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex min-h-42 flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-gray-300 bg-white/60 p-5 text-gray-500 hover:border-blue-500 hover:text-blue-500"
    >
      <span className="iconoir-plus text-2xl" aria-hidden="true" />
      <span className="text-sm font-semibold">Add report</span>
    </button>
  );
}
